import { formatCurrency } from './formatCurrency';

export interface CurrencyAmountProps {
  /** Raw amount. Formatting is this component's job, not the caller's. */
  readonly amount: number | string | null | undefined;
  /**
   * ISO 4217 code from the API. Required — never inferred.
   *
   * The prop is required so every call site must pass one — a site that cannot is a site to fix —
   * but the value may be nullish, because payload shapes still exist that do not carry it. An
   * absent or malformed code degrades to `placeholder` rather than throwing.
   */
  readonly currency: string | null | undefined;
  readonly notation?: 'standard' | 'compact';
  /**
   * `accounting` renders negatives in parentheses — `($1,234.50)` — the convention these
   * surfaces used for return premiums before this component existed.
   */
  readonly currencySign?: 'standard' | 'accounting';
  readonly minimumFractionDigits?: number;
  readonly maximumFractionDigits?: number;
  /** Shown when `amount` is missing or unparseable. */
  readonly placeholder?: string;
  /** Test and Storybook seam only; app code lets `getLocale()` decide. */
  readonly locale?: string | readonly string[];
}

export const CurrencyAmount = ({
  amount,
  currency,
  notation = 'standard',
  currencySign = 'standard',
  minimumFractionDigits,
  maximumFractionDigits,
  placeholder = '-',
  locale,
}: CurrencyAmountProps) => {
  const formatted = formatCurrency(amount, currency, {
    notation,
    currencySign,
    minimumFractionDigits,
    maximumFractionDigits,
    locale,
  });

  return <>{formatted ?? placeholder}</>;
};

CurrencyAmount.displayName = 'CurrencyAmount';
